
import { useState } from 'react';

interface StateMapProps {
  onStateSelect: (state: string) => void;
}

export default function StateMap({ onStateSelect }: StateMapProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedStateName, setSelectedStateName] = useState('');

  const states = [
    { name: 'Alabama', code: 'AL', counties: 67 },
    { name: 'Alaska', code: 'AK', counties: 29 },
    { name: 'Arizona', code: 'AZ', counties: 15 },
    { name: 'Arkansas', code: 'AR', counties: 75 },
    { name: 'California', code: 'CA', counties: 58 },
    { name: 'Colorado', code: 'CO', counties: 64 },
    { name: 'Connecticut', code: 'CT', counties: 8 },
    { name: 'Delaware', code: 'DE', counties: 3 },
    { name: 'Florida', code: 'FL', counties: 67 },
    { name: 'Georgia', code: 'GA', counties: 159 },
    { name: 'Hawaii', code: 'HI', counties: 5 },
    { name: 'Idaho', code: 'ID', counties: 44 },
    { name: 'Illinois', code: 'IL', counties: 102 },
    { name: 'Indiana', code: 'IN', counties: 92 },
    { name: 'Iowa', code: 'IA', counties: 99 },
    { name: 'Kansas', code: 'KS', counties: 105 },
    { name: 'Kentucky', code: 'KY', counties: 120 },
    { name: 'Louisiana', code: 'LA', counties: 64 },
    { name: 'Maine', code: 'ME', counties: 16 },
    { name: 'Maryland', code: 'MD', counties: 24 },
    { name: 'Massachusetts', code: 'MA', counties: 14 },
    { name: 'Michigan', code: 'MI', counties: 83 },
    { name: 'Minnesota', code: 'MN', counties: 87 },
    { name: 'Mississippi', code: 'MS', counties: 82 },
    { name: 'Missouri', code: 'MO', counties: 115 },
    { name: 'Montana', code: 'MT', counties: 56 },
    { name: 'Nebraska', code: 'NE', counties: 93 },
    { name: 'Nevada', code: 'NV', counties: 17 },
    { name: 'New Hampshire', code: 'NH', counties: 10 },
    { name: 'New Jersey', code: 'NJ', counties: 21 },
    { name: 'New Mexico', code: 'NM', counties: 33 },
    { name: 'New York', code: 'NY', counties: 62 },
    { name: 'North Carolina', code: 'NC', counties: 100 },
    { name: 'North Dakota', code: 'ND', counties: 53 },
    { name: 'Ohio', code: 'OH', counties: 88 },
    { name: 'Oklahoma', code: 'OK', counties: 77 },
    { name: 'Oregon', code: 'OR', counties: 36 },
    { name: 'Pennsylvania', code: 'PA', counties: 67 },
    { name: 'Rhode Island', code: 'RI', counties: 5 },
    { name: 'South Carolina', code: 'SC', counties: 46 },
    { name: 'South Dakota', code: 'SD', counties: 66 },
    { name: 'Tennessee', code: 'TN', counties: 95 },
    { name: 'Texas', code: 'TX', counties: 254 },
    { name: 'Utah', code: 'UT', counties: 29 },
    { name: 'Vermont', code: 'VT', counties: 14 },
    { name: 'Virginia', code: 'VA', counties: 134 },
    { name: 'Washington', code: 'WA', counties: 39 },
    { name: 'West Virginia', code: 'WV', counties: 55 },
    { name: 'Wisconsin', code: 'WI', counties: 72 },
    { name: 'Wyoming', code: 'WY', counties: 23 }
  ];

  const handleStateSelect = (state: { name: string; code: string; counties: number }) => {
    setSelectedStateName(state.name);
    setIsDropdownOpen(false);
    onStateSelect(state.code);
  };

  return (
    <section className="bg-slate-800 py-16 border-b border-slate-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-white mb-4">Select Your State</h2>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            Choose a state to view county assessor offices and appraisal districts
          </p>
        </div>

        {/* State Dropdown */}
        <div className="max-w-md mx-auto mb-12">
          <div className="relative">
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="w-full bg-slate-700 hover:bg-slate-600 border border-slate-600 hover:border-emerald-500 rounded-lg px-4 py-4 text-left transition-all duration-300 cursor-pointer"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <i className="ri-map-pin-line w-5 h-5 flex items-center justify-center text-emerald-400 mr-3"></i>
                  <span className="text-white font-medium">
                    {selectedStateName || 'Choose a state...'}
                  </span>
                </div>
                <i className={`ri-arrow-down-s-line w-5 h-5 flex items-center justify-center text-slate-400 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
              </div>
            </button>

            {isDropdownOpen && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-slate-700 border border-slate-600 rounded-lg shadow-xl z-50 max-h-80 overflow-y-auto">
                {states.map((state) => (
                  <button
                    key={state.code}
                    onClick={() => handleStateSelect(state)}
                    className="w-full px-4 py-3 text-left hover:bg-slate-600 transition-colors cursor-pointer border-b border-slate-600 last:border-b-0"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-white font-medium">{state.name}</span>
                        <span className="text-slate-400 text-sm ml-2">({state.code})</span>
                      </div>
                      <span className="text-slate-400 text-sm">{state.counties} counties</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Popular States Quick Access */}
        <div className="mt-12">
          <h3 className="text-xl font-semibold text-white mb-6 text-center">Popular States</h3>
          <div className="flex flex-wrap justify-center gap-4">
            {['CA', 'TX', 'FL', 'NY', 'PA', 'IL', 'OH', 'GA'].map((stateCode) => {
              const state = states.find(s => s.code === stateCode);
              return (
                <button
                  key={stateCode}
                  onClick={() => handleStateSelect(state!)}
                  className="bg-emerald-600 hover:bg-emerald-500 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap"
                >
                  {state?.name} ({state?.counties})
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
